import React, { useState, useEffect, useContext } from "react";
import { FaWhatsapp, FaTimes, FaBoxes, FaLayerGroup, FaUser, FaPhoneAlt, FaMapMarkerAlt, FaCommentDots } from "react-icons/fa";
import { generateSingleProductWholesaleUrl, generateMultiProductWholesaleUrl } from "../utils/whatsappHelper";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import "../styles/BulkInquiryModal.css";

const PRESET_QUANTITIES = [25, 50, 100, 250, 500];

const getVariantDisplay = (v, fallbackProduct = null) => {
  if (!v && !fallbackProduct) return "";
  const target = v || fallbackProduct;
  if (typeof target === "string") return target;
  if (target.variant_label) return target.variant_label;
  if (target.variant_details) return target.variant_details;
  if (target.variant) return target.variant;
  if (target.quantity && target.quantity_unit) {
    return ["size", "color"].includes(target.quantity_unit?.toLowerCase())
      ? `${target.quantity}`
      : `${target.quantity} ${target.quantity_unit}`;
  }
  return "";
};

const BulkInquiryModal = ({
  isOpen,
  onClose,
  product = null,
  variant = null,
  items = [], // For multi-product cart mode
}) => {
  const { user } = useContext(AuthContext);

  const [quantity, setQuantity] = useState("50");
  const [multiItems, setMultiItems] = useState([]);
  const [buyerName, setBuyerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [cityState, setCityState] = useState("");
  const [specialRequirements, setSpecialRequirements] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMulti = items && items.length > 0;

  // Initialize form when modal transitions from closed to open
  useEffect(() => {
    if (!isOpen) return;

    // Pre-fill user info if logged in
    const defaultName =
      user?.customer_details?.full_name ||
      user?.customer_details?.name ||
      user?.full_name ||
      user?.first_name ||
      user?.username ||
      "";

    const defaultPhone =
      user?.customer_details?.contact_number ||
      user?.customer_details?.phone_number ||
      user?.phone ||
      user?.phone_number ||
      "";

    let defaultCity = user?.customer_details?.city || user?.city || "";
    if (!defaultCity && user?.customer_details?.address) {
      try {
        const addr =
          typeof user.customer_details.address === "string" &&
          user.customer_details.address.startsWith("{")
            ? JSON.parse(user.customer_details.address)
            : null;
        if (addr && (addr.city || addr.state)) {
          defaultCity = [addr.city, addr.state].filter(Boolean).join(", ");
        }
      } catch (e) {}
    }

    setBuyerName(defaultName);
    setPhoneNumber(defaultPhone);
    setCityState(defaultCity);
    setSpecialRequirements("");

    if (items && items.length > 0) {
      setMultiItems(
        items.map((item) => ({
          id: item.id || item.product_id || item.product || null,
          name: item.product_name || item.name || "Product",
          product_image: item.product_image || "",
          price: item.wholesale_price || item.price || null,
          variant: getVariantDisplay(item),
          qty: String(Math.max(Number(item.qty) || 1, 10)),
        }))
      );
    } else {
      setQuantity("50");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleMultiQtyChange = (index, value) => {
    setMultiItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], qty: value };
      return updated;
    });
  };

  const handleSendInquiry = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!buyerName.trim()) {
      toast.error("Please enter your name or business name.");
      return;
    }

    if (!phoneNumber.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }

    let whatsappUrl = "";
    let payload = null;

    if (isMulti) {
      if (multiItems.length === 0) {
        toast.error("No items in cart for inquiry.");
        return;
      }

      const formattedItems = multiItems.map((item) => ({
        product_id: item.id ? Number(item.id) : null,
        product_name: item.name || "Product",
        product_image: item.product_image || "",
        variant_details: item.variant || "",
        quantity: Math.max(1, parseInt(item.qty, 10) || 1),
        unit_price:
          item.price !== null && item.price !== undefined && !isNaN(Number(item.price))
            ? Number(Number(item.price).toFixed(2))
            : 0.0,
      }));

      const totalUnits = formattedItems.reduce((acc, curr) => acc + curr.quantity, 0);

      payload = {
        inquiry_type: "multi",
        buyer_name: buyerName.trim(),
        phone_number: phoneNumber.trim(),
        city_state: cityState.trim(),
        special_requirements: specialRequirements.trim(),
        total_quantity: totalUnits,
        total_products: formattedItems.length,
        items: formattedItems,
      };

      whatsappUrl = generateMultiProductWholesaleUrl({
        items: formattedItems.map((item) => ({
          name: item.product_name,
          variant: item.variant_details,
          qty: item.quantity,
        })),
        buyerName: buyerName.trim(),
        phoneNumber: phoneNumber.trim(),
        buyerCity: cityState.trim(),
        notes: specialRequirements.trim(),
      });
    } else if (product) {
      const parsedQty = Math.max(1, parseInt(quantity, 10) || 1);
      const unitPriceVal =
        product.wholesale_price || product.price || (variant && variant.price) || 0;

      const singleItem = {
        product_id: product.id ? Number(product.id) : null,
        product_name: product.product_name || product.name || "Product",
        product_image: product.product_image || "",
        variant_details: getVariantDisplay(variant, product),
        quantity: parsedQty,
        unit_price:
          unitPriceVal !== null && unitPriceVal !== undefined && !isNaN(Number(unitPriceVal))
            ? Number(Number(unitPriceVal).toFixed(2))
            : 0.0,
      };

      payload = {
        inquiry_type: "single",
        buyer_name: buyerName.trim(),
        phone_number: phoneNumber.trim(),
        city_state: cityState.trim(),
        special_requirements: specialRequirements.trim(),
        total_quantity: parsedQty,
        total_products: 1,
        items: [singleItem],
      };

      whatsappUrl = generateSingleProductWholesaleUrl({
        productName: singleItem.product_name,
        variant: singleItem.variant_details,
        quantity: parsedQty,
        buyerName: buyerName.trim(),
        phoneNumber: phoneNumber.trim(),
        buyerCity: cityState.trim(),
        notes: specialRequirements.trim(),
      });
    }

    // Save inquiry to backend API
    setIsSubmitting(true);
    try {
      if (payload) {
        const res = await api.post("wholesale-inquiry/", payload);
        if (res.data && res.data.status === false) {
          throw new Error(res.data.message || "Failed to submit inquiry.");
        }
        toast.success("Wholesale inquiry submitted successfully!");
      }
    } catch (err) {
      console.error("Failed to save wholesale inquiry:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.errors?.phone_number?.[0] ||
        err.message ||
        "Could not save inquiry to server, opening WhatsApp directly.";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
      if (whatsappUrl) {
        window.open(whatsappUrl, "_blank");
      }
      onClose();
    }
  };

  const totalMultiUnits = isMulti
    ? multiItems.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0)
    : parseInt(quantity, 10) || 0;

  const variantText = getVariantDisplay(variant, product);

  return (
    <div className="bulk-modal-backdrop" onClick={onClose}>
      <div
        className="bulk-modal-container"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="bulk-modal-header">
          <div className="bulk-modal-title-wrap">
            <FaWhatsapp size={22} color="#25D366" />
            <h5>Bulk & Wholesale Inquiry</h5>
            <span className="bulk-modal-badge">Wholesale</span>
          </div>
          <button
            className="bulk-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSendInquiry} className="bulk-modal-body">
          {/* Selected Product(s) Display */}
          {isMulti ? (
            <div className="bulk-form-group">
              <label className="bulk-form-label">
                <FaLayerGroup style={{ marginRight: 6 }} />
                Selected Products ({multiItems.length} items)
              </label>
              <div className="bulk-items-list-container">
                {multiItems.map((item, idx) => (
                  <div key={idx} className="bulk-list-item-row">
                    <div className="bulk-item-details">
                      {item.product_image && (
                        <img
                          src={item.product_image}
                          alt={item.name}
                          className="bulk-item-thumb"
                        />
                      )}
                      <div>
                        <p className="bulk-item-title">{item.name}</p>
                        {item.variant && (
                          <p className="bulk-item-variant">{item.variant}</p>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span style={{ fontSize: "0.78rem", color: "#64748b" }}>Qty:</span>
                      <input
                        type="number"
                        min="1"
                        className="bulk-item-qty-input"
                        value={item.qty}
                        onChange={(e) => handleMultiQtyChange(idx, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : product ? (
            <div className="bulk-product-preview-card">
              {product.product_image && (
                <img
                  src={product.product_image}
                  alt={product.product_name}
                  className="bulk-preview-img"
                />
              )}
              <div className="bulk-preview-info">
                <p className="bulk-preview-name">{product.product_name}</p>
                {variantText && (
                  <p className="bulk-preview-meta">
                    <span>Variant: {variantText}</span>
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {/* Single product quantity selector */}
          {!isMulti && (
            <div className="bulk-form-group">
              <label className="bulk-form-label">
                <FaBoxes style={{ marginRight: 6 }} />
                Wholesale Quantity Needed *
              </label>
              <input
                type="number"
                min="1"
                required
                className="bulk-form-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g. 50"
              />
              <div className="bulk-preset-chips">
                {PRESET_QUANTITIES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className={`bulk-chip-btn ${String(quantity) === String(q) ? "active" : ""}`}
                    onClick={() => setQuantity(String(q))}
                  >
                    {q}+ units
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Buyer & Business Information */}
          <div className="row g-2">
            <div className="col-sm-6 bulk-form-group">
              <label className="bulk-form-label">
                <FaUser style={{ marginRight: 5, fontSize: "0.8rem", color: "#64748b" }} />
                Your Name / Business Name *
              </label>
              <input
                type="text"
                required
                className="bulk-form-input"
                placeholder="e.g. Vijay Kumar or ABC Store"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
              />
            </div>
            <div className="col-sm-6 bulk-form-group">
              <label className="bulk-form-label">
                <FaPhoneAlt style={{ marginRight: 5, fontSize: "0.8rem", color: "#64748b" }} />
                Phone / WhatsApp Number *
              </label>
              <input
                type="tel"
                required
                className="bulk-form-input"
                placeholder="e.g. 9876543210"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="bulk-form-group">
            <label className="bulk-form-label">
              <FaMapMarkerAlt style={{ marginRight: 5, fontSize: "0.8rem", color: "#64748b" }} />
              City / State
            </label>
            <input
              type="text"
              className="bulk-form-input"
              placeholder="e.g. Salem, Tamil Nadu"
              value={cityState}
              onChange={(e) => setCityState(e.target.value)}
            />
          </div>

          {/* Special Requirements / Notes */}
          <div className="bulk-form-group">
            <label className="bulk-form-label">
              <FaCommentDots style={{ marginRight: 5, fontSize: "0.8rem", color: "#64748b" }} />
              Special Requirements / Notes (Optional)
            </label>
            <textarea
              className="bulk-form-textarea"
              placeholder="e.g. Delivery needed before 10th, custom sticker labels"
              value={specialRequirements}
              onChange={(e) => setSpecialRequirements(e.target.value)}
            />
          </div>

          {/* Footer inside form */}
          <div className="bulk-modal-footer">
            <p className="bulk-summary-text" style={{ margin: 0, fontWeight: 600 }}>
              Total:{" "}
              <span className="bulk-summary-highlight">
                {totalMultiUnits} {totalMultiUnits === 1 ? "unit" : "units"}
              </span>
              {isMulti && ` (${multiItems.length} products)`}
            </p>
            <button type="submit" className="bulk-submit-btn" disabled={isSubmitting}>
              <FaWhatsapp size={18} />
              {isSubmitting ? "Submitting Inquiry..." : "Submit Wholesale Inquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkInquiryModal;
